import { useEffect, useState } from 'react';

import { cn } from '@mds/shared/lib/utils';

/** How long each word stays before the next replaces it. */
const CYCLE_MS = 2400;
/**
 * How long the leaving word gets before it is swapped. A beat past
 * `--dur-fast`, the fade it runs on, so the swap happens once it is gone.
 */
const SWAP_MS = 280;

/**
 * A line that ends on a word that keeps changing: "We're always looking for
 * baristas.", then "community hosts.", and on.
 *
 * The word turns over vertically, like a row on a flip board: the old one tips
 * forward around its horizontal axis, is swapped while edge-on, and the new
 * one flips down into place from above. Under reduced motion it still changes, without
 * the turn.
 *
 * A screen reader gets the whole list as one sentence instead, since a word
 * that changes every few seconds would either be read over and over or never
 * read past the first.
 */
export default function RotatingWord({
  lead,
  words,
  className,
}: {
  /** The fixed start of the line. */
  lead: string;
  words: readonly string[];
  className?: string;
}) {
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (words.length < 2) return;
    let swap: ReturnType<typeof setTimeout>;
    const cycle = setInterval(() => {
      setLeaving(true);
      swap = setTimeout(() => {
        setIndex((i) => (i + 1) % words.length);
        setLeaving(false);
      }, SWAP_MS);
    }, CYCLE_MS);
    return () => {
      clearInterval(cycle);
      clearTimeout(swap);
    };
  }, [words.length]);

  return (
    <p className={cn('text-body-lg font-medium text-ink-secondary', className)}>
      <span className="sr-only">
        {lead} {words.join(', ')}
      </span>
      {/* `perspective` so the turn reads as depth, not as the word squashing
          flat. A block, because an inline span cannot carry a perspective;
          it is the paragraph's only visible content, so nothing moves. */}
      <span aria-hidden="true" className="block perspective-normal">
        {lead}{' '}
        {/* Keyed by the word, so each new one mounts and runs the flip in;
            the flip out is a transition to edge-on. */}
        <span
          key={index}
          className={cn(
            // `nowrap`: an inline-block wraps inside itself, which stacked
            // "curious" over "people.". Kept whole, a phrase that does not fit
            // moves down a line instead.
            'inline-block whitespace-nowrap text-brand-text',
            'animate-word-flip-in transition-[transform,opacity] duration-(--duration-fast) ease-standard',
            'motion-reduce:animate-none motion-reduce:transition-none',
            leaving ? '-rotate-x-90 opacity-0' : 'rotate-x-0 opacity-100',
          )}
        >
          {words[index]}
        </span>
      </span>
    </p>
  );
}
